// SVG charts on recharts 3 (ADR 0002). Pages never import recharts; they
// import these. Every axis, grid line, and label is styled from tokens in
// console.css (SVG text and lines take CSS), series colours are token
// custom properties, and recharts' own animation is off: values never move
// on their own, the panel's entrance is the only motion.
import type { CSSProperties, ReactNode } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Tooltip, XAxis, YAxis, type XAxisProps } from "recharts";
import {
  ChartFigure,
  ChartTooltip,
  defaultFormat,
  SERIES_COLORS,
  SERIES_CONTEXT,
  SERIES_MAIN,
  yAxisWidth,
  type ChartBaseProps,
  type ChartHeight,
  type ValueFormat,
} from "./frame";
import type { CategoryDatum } from "./Bars";

const FILL = { width: "100%", height: "100%" } as const;
const TICK = { fontSize: 13 } as const;

interface EdgeTickProps {
  x?: number | string;
  y?: number | string;
  payload?: { value?: unknown };
  first?: string;
  last?: string;
  format?: (v: string) => string;
}

/**
 * An x tick that anchors the first label to its start and the last to its
 * end, so the outermost labels always sit inside the plot and never clip.
 */
function EdgeTick({ x = 0, y = 0, payload, first, last }: EdgeTickProps) {
  const v = String(payload?.value ?? "");
  const anchor = v === first ? "start" : v === last ? "end" : "middle";
  return (
    <text x={Number(x)} y={Number(y)} dy={16} textAnchor={anchor} className="con-tick">
      {v}
    </text>
  );
}

/**
 * Advance of every character class of Geist at --text-n1 (13px, tabular
 * figures), in px, measured in Chrome and rounded up to the widest member of
 * the class. A character in no class counts as the widest glyph measured.
 */
const ADVANCES: ReadonlyArray<readonly [chars: string, px: number]> = [
  ["i',.!", 3],
  [" jIl|():;`", 3.9],
  ['ftr"', 4.6],
  ["-/\\[]{}*^", 5.6],
  ["szvcykxaeL#", 7.2],
  ["nouhgbdpqJFTZ?+=<>_~", 7.7],
  ["0123456789EY", 7.8],
  ["XKPS&$", 8.4],
  ["VARBUHGCD", 9.2],
  ["NQO", 9.7],
  ["w%", 10.7],
  ["mM", 11.2],
  ["W@", 12.2],
];
const ADVANCE_WIDEST = 14.2;
/**
 * Kerning between unlike characters adds to a sum of advances. Over 40,000
 * measured strings of 2 to 12 characters a label drew at most 1.44px wider
 * than the sum of its classes, so the bound adds 2% and 1.5px, twice that.
 */
const ADVANCE_SLACK = 1.02;
const ADVANCE_KERN = 1.5;

const ADVANCE_OF = new Map<string, number>(ADVANCES.flatMap(([chars, px]) => [...chars].map((char) => [char, px] as const)));

/**
 * An upper bound, in px, of the width a tick or axis label draws at: Geist at
 * --text-n1. It is what lets a chart decide in its markup, before anything is
 * measured in a browser, whether a label is certain to fit where it is put.
 */
export function textWidthBound(text: string): number {
  let width = 0;
  for (const char of text) width += ADVANCE_OF.get(char) ?? ADVANCE_WIDEST;
  return width * ADVANCE_SLACK + ADVANCE_KERN;
}

/**
 * Y axis width that fits the tick the axis rounds up to, not only the data.
 * An axis whose largest value is 950 draws its top tick at 1.050, two
 * characters longer than any value. recharts rounds the step between ticks
 * up by at most half, so the top tick never reaches twice the largest value:
 * the width is taken from that double as well, on both sides of zero when a
 * value is negative.
 */
export function tickAxisWidth(values: number[], format: ValueFormat): number {
  const peak = values.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
  const ceiling = Math.ceil(peak * 2);
  const below = values.some((v) => v < 0) ? [-ceiling] : [];
  return yAxisWidth([...values, ceiling, ...below], format);
}

// ---------------------------------------------------------------------------
// The category axis of the column charts (ColumnChart, GroupedColumnChart,
// ComboChart). It prints a label in one of three ways, decided from the labels
// alone, so a page can ask the same question before it renders:
//
//   plain   every label is 8 characters or less: one line, exactly as the
//           column chart has always drawn it (recharts thins a crowded axis)
//   wrap    a longer label: up to two lines, broken tidily at a word
//           boundary, and every tick printed. The names need a chart of a
//           certain width; below it the axis is numbered and the names are
//           listed under the plot, in full, so nothing is cut and no tick is
//           dropped; and where the chart is wide enough for every label on
//           one line, each has its one line
//   number  a label that no wrapped tick can carry (too long, or too many
//           categories): the axis is numbered at every width, names listed
// ---------------------------------------------------------------------------

/**
 * How far under the axis line a category label starts. In a narrow card the
 * first label is wider than its band and runs left under the y axis column;
 * at 8px its box starts below the box of the "0" tick there (measured: the
 * tick ends 8.6px under the line, the label starts at 10.2px and ends at
 * 27.2px, inside the 28px the axis reserves), so the two never touch.
 */
export const CATEGORY_TICK_MARGIN = 8;

/** A label this short keeps the single line it always had. */
const PLAIN_LABEL_MAX = 8;

/**
 * The longest label, in characters, a wrapped tick carries: two lines of about
 * a dozen characters. A longer label is not cut: the axis is numbered and the
 * label is listed in full under the plot.
 */
export const COLUMN_LABEL_MAX = 24;

/**
 * The most categories a plot of the narrowest width can print without
 * dropping a tick. A chart in a panel on a 320px phone is 254px wide; less
 * its y axis that is about 212px, so eight bands are 26px each, room for a
 * three character label ("Jan", "600") and the gap to its neighbour. Past
 * eight, labels longer than 8 characters are numbered at every width.
 */
export const COLUMN_CATEGORIES_MAX = 8;

/**
 * The y axis width columnLabelsFit assumes when it is given none: 48px, which
 * holds ticks up to "9.999". The chart itself uses the width its values need.
 */
export const COLUMN_AXIS_RESERVE = 48;

/**
 * The chart widths, in px, at which a wrapped axis may switch from numbers to
 * names. The first is the narrowest chart the kit plans for (a 320px phone
 * gives 254px); console.css carries one container rule per entry.
 */
export const COLUMN_NAME_TIERS = [248, 288, 328, 368, 408, 448, 512, 576, 640, 720, 800, 880] as const;

/** Clear space between two neighbouring labels, and the plot's end margin. */
const NAME_GAP = 4;
const PLOT_END = 4;
/** What the axis reserves under the plot: one line of ticks, and a second line when a label wraps. */
const AXIS_LINE = 28;
const AXIS_SECOND_LINE = 17;

export interface CategoryAxisPlan {
  mode: "plain" | "wrap" | "number";
  /** In "wrap": the chart width in px from which the names print. Below it the axis is numbered. */
  namesFrom?: number;
  /**
   * In "wrap", when a label takes two lines: the chart width in px from which
   * every label fits on one line and is printed on one. Undefined when no
   * card is that wide.
   */
  wholeFrom?: number;
  /** Each label as the one or two lines it prints on where it has to wrap. */
  lines: string[][];
}

/** A word this short is not left alone on a line when it can sit with its neighbour. */
const SHORT_WORD = 3;
/** A word this short after a number is its unit ("2 jt", "48 jam", "5 kg") and stays with it. */
const UNIT_WORD = 4;

/**
 * Whether a line may break before word `at`. Never before a joining mark (a
 * dash, a slash, an ampersand: the mark ends the line it joins from), never
 * after a comparison mark ("<=", ">": it belongs to what follows), and never
 * between a number and its unit.
 */
function mayBreakBefore(words: string[], at: number): boolean {
  const before = words[at - 1]!;
  const after = words[at]!;
  if (/^[^\p{L}\p{N}]+$/u.test(after)) return false;
  if (/^[<>~]=?$/.test(before)) return false;
  if (/^\d/.test(before) && after.length <= UNIT_WORD && /^\p{L}+\.?$/u.test(after)) return false;
  return true;
}

/**
 * A label on the fewest lines that fit `room` px, up to `maxLines`, broken
 * only where mayBreakBefore allows. Among breaks of the same number of lines
 * it takes the one that leaves no short word alone on a line, and then the
 * most balanced: the one whose longest line is shortest. Null when the label
 * cannot be set that way; it is never cut.
 */
export function tidyLines(label: string, room: number, maxLines: number): string[] | null {
  const words = label.split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  // Beyond a dozen words a label is a sentence, not a label: try it whole and stop.
  const gaps = Math.min(words.length - 1, 11);
  let best: { lines: string[]; score: [number, number, number] } | null = null;
  for (let mask = 0; mask < 1 << gaps; mask++) {
    const lines: string[] = [];
    let start = 0;
    let allowed = true;
    for (let at = 1; at <= words.length && allowed; at++) {
      const breakHere = at === words.length || (at <= gaps && (mask & (1 << (at - 1))) !== 0);
      if (!breakHere) continue;
      if (at < words.length && !mayBreakBefore(words, at)) allowed = false;
      lines.push(words.slice(start, at).join(" "));
      start = at;
    }
    if (!allowed || lines.length > maxLines) continue;
    const widest = Math.max(...lines.map(textWidthBound));
    if (widest > room) continue;
    const alone = lines.length > 1 ? lines.filter((line) => !line.includes(" ") && line.length <= SHORT_WORD).length : 0;
    const score: [number, number, number] = [lines.length, alone, widest];
    if (best === null || score[0] < best.score[0] || (score[0] === best.score[0] && (score[1] < best.score[1] || (score[1] === best.score[1] && score[2] < best.score[2])))) best = { lines, score };
  }
  return best === null ? null : best.lines;
}

/**
 * How a category label breaks on its own, whatever its neighbours do: one
 * line, or two tidy ones. A label of 8 characters or less is never broken
 * ("<= 1 jt", "> 6 jt"). A longer one breaks in two only where the break is
 * tidy: not before a joining mark, not between a number and its unit, and
 * never leaving a short word alone on a line ("Gelombang 1" stays whole);
 * of the tidy breaks it takes the most balanced ("1 jt - 2 jt" becomes
 * "1 jt -" over "2 jt"). With no tidy break the label keeps one line.
 */
export function tidyBreak(label: string): string[] {
  const whole = label.split(/\s+/).filter(Boolean).join(" ");
  if (whole.length <= PLAIN_LABEL_MAX) return [whole];
  const words = whole.split(" ");
  let best: { lines: string[]; width: number } | null = null;
  for (let at = 1; at < words.length; at++) {
    if (!mayBreakBefore(words, at)) continue;
    const lines = [words.slice(0, at).join(" "), words.slice(at).join(" ")];
    if (lines.some((line) => !line.includes(" ") && line.length <= SHORT_WORD)) continue;
    const width = Math.max(...lines.map(textWidthBound));
    if (best === null || width < best.width) best = { lines, width };
  }
  return best !== null ? best.lines : [whole];
}

/**
 * The break a label takes only where it cannot have its one line: the most
 * balanced one the joining marks and the units allow, even when it leaves a
 * short word alone ("Gelombang" over "1"). Two words that fit together stay
 * together; this is for the widths at which they do not.
 */
function looseBreak(label: string): string[] {
  const words = label.split(/\s+/).filter(Boolean);
  let best: { lines: string[]; width: number } | null = null;
  for (let at = 1; at < words.length; at++) {
    if (!mayBreakBefore(words, at)) continue;
    const lines = [words.slice(0, at).join(" "), words.slice(at).join(" ")];
    const width = Math.max(...lines.map(textWidthBound));
    if (best === null || width < best.width) best = { lines, width };
  }
  return best !== null ? best.lines : [words.join(" ")];
}

/**
 * The narrowest tier at which every line of every label has its own band:
 * the widest line plus the gap, once per category, plus the y axis and the
 * plot's end margin. Null when no tier is wide enough.
 */
function nameTier(lines: string[][], axisWidth: number): number | null {
  if (lines.length === 0) return null;
  const widest = Math.ceil(Math.max(0, ...lines.flat().map(textWidthBound)));
  const need = lines.length * (widest + NAME_GAP) + axisWidth + PLOT_END;
  return COLUMN_NAME_TIERS.find((tier) => tier >= need) ?? null;
}

/** How the category axis prints these labels beside a y axis of this width. */
export function planCategoryAxis(labels: string[], axisWidth: number): CategoryAxisPlan {
  const names = labels.map((label) => label.trim());
  if (names.every((name) => name.length <= PLAIN_LABEL_MAX)) return { mode: "plain", lines: names.map((name) => [name]) };
  const numbered: CategoryAxisPlan = { mode: "number", lines: names.map((name) => [name]) };
  // More categories than a narrow plot can number without the numbers touching, or a label too long.
  if (names.length > COLUMN_CATEGORIES_MAX || names.some((name) => name.length > COLUMN_LABEL_MAX)) return numbered;
  // Where the labels have to wrap, each breaks on its own terms: tidily when
  // it can (tidyBreak), loosely when it has no tidy break, never when it is 8
  // characters or less. One that is no wider than the widest line the axis
  // needs anyway keeps its one line while its neighbours wrap.
  const wholes = names.map((name) => name.split(/\s+/).filter(Boolean).join(" "));
  const breaks = wholes.map((name) => {
    const tidy = tidyBreak(name);
    return tidy.length > 1 || name.length <= PLAIN_LABEL_MAX ? tidy : looseBreak(name);
  });
  const widest = Math.max(...breaks.flat().map(textWidthBound));
  const lines = wholes.map((name, i) => (textWidthBound(name) <= widest ? [name] : breaks[i]!));
  const namesFrom = nameTier(lines, axisWidth);
  if (namesFrom === null) return numbered;
  // Where the chart is wide enough for it, every label has its one line.
  const wraps = lines.some((label) => label.length > 1);
  const wholeFrom = wraps ? (nameTier(wholes.map((name) => [name]), axisWidth) ?? undefined) : undefined;
  return { mode: "wrap", namesFrom, wholeFrom, lines };
}

/**
 * The chart width, in px, from which a column chart prints every one of these
 * labels as a name under its column, or null when it numbers the axis at
 * every width. A chart in a panel is about 254px wide in a third of the
 * twelve columns at 1280 and on a 320px phone, 418px in a half, 910px in the
 * full width. `axisWidth` is the y axis the chart will draw (tickAxisWidth
 * gives it for a set of values); the default holds ticks up to "9.999".
 *
 * Labels of 8 characters or less are the long standing case: the chart draws
 * them on one line at any width and lets recharts thin a crowded axis, and
 * this returns the width from which none is thinned.
 */
export function columnNamesFrom(labels: string[], axisWidth: number = COLUMN_AXIS_RESERVE): number | null {
  const plan = planCategoryAxis(labels, axisWidth);
  if (plan.mode === "plain") return nameTier(plan.lines, axisWidth);
  return plan.mode === "wrap" ? (plan.namesFrom ?? null) : null;
}

/**
 * Whether a column chart can print every one of these labels at the narrowest
 * width, a 320px phone inside a panel: at most COLUMN_CATEGORIES_MAX of them,
 * none longer than COLUMN_LABEL_MAX characters, and every line of every label
 * inside its own band. A page asks this before it picks columns over another
 * mark. When it is false the chart still draws every column and still shows
 * every label in full: it numbers the axis and lists the names under the
 * plot, wherever the chart is too narrow for them. columnNamesFrom says from
 * which width that is.
 */
export function columnLabelsFit(labels: string[], axisWidth: number = COLUMN_AXIS_RESERVE): boolean {
  return labels.length <= COLUMN_CATEGORIES_MAX && columnNamesFrom(labels, axisWidth) === COLUMN_NAME_TIERS[0];
}

interface CategoryTickProps {
  x?: number | string;
  y?: number | string;
  payload?: { index?: number };
  index?: number;
  /** The lines of every label by position. Omitted on a numbered axis. */
  lines?: string[][];
  /** Draw the one line form too, for the widths at which every label fits on one line. */
  whole?: boolean;
}

/**
 * One category tick. On a wrapped axis it draws every form of the label at
 * the band centre, the name on one line, the name on its one or two lines,
 * and the category number, and console.css shows exactly one: the one line
 * where every label has room for it, the wrapped name from the chart width
 * the plan worked out, the number below that. On a numbered axis it draws
 * the number.
 */
function CategoryTick({ x = 0, y = 0, payload, index = 0, lines, whole = false }: CategoryTickProps) {
  const position = payload?.index ?? index;
  const at = { x: Number(x), y: Number(y), dy: "0.71em", textAnchor: "middle" as const };
  const name = lines?.[position];
  return (
    <g>
      {name && whole ? (
        <text {...at} className="con-tick con-col-whole">
          {name.join(" ")}
        </text>
      ) : null}
      {name ? (
        <text {...at} className="con-tick con-col-name">
          {name.map((line, i) => (
            <tspan key={i} x={at.x} dy={i === 0 ? undefined : "1.3em"}>
              {line}
            </tspan>
          ))}
        </text>
      ) : null}
      <text {...at} className="con-tick con-col-index">
        {position + 1}
      </text>
    </g>
  );
}

/**
 * The XAxis props of a wrapped or numbered category axis. Wrapped, every tick
 * is printed and the axis grows by a line when a label takes two. Numbered,
 * recharts thins the numbers like any ordered scale; the tick formatter gives
 * it the width of the number to thin by, and the tick draws the number.
 */
export function categoryAxisProps(plan: CategoryAxisPlan): Pick<XAxisProps, "dataKey" | "tickLine" | "tick" | "interval" | "minTickGap" | "tickMargin" | "height" | "tickFormatter"> {
  if (plan.mode === "wrap") {
    const second = plan.lines.some((lines) => lines.length > 1);
    return { dataKey: "label", tickLine: false, tick: <CategoryTick lines={plan.lines} whole={plan.wholeFrom !== undefined} />, interval: 0, tickMargin: CATEGORY_TICK_MARGIN, height: AXIS_LINE + (second ? AXIS_SECOND_LINE : 0) };
  }
  return {
    dataKey: "label",
    tickLine: false,
    tick: <CategoryTick />,
    interval: "preserveStartEnd",
    minTickGap: NAME_GAP,
    tickMargin: CATEGORY_TICK_MARGIN,
    height: AXIS_LINE,
    tickFormatter: (_value: unknown, index: number) => String(index + 1),
  };
}

/**
 * The width one column of a numbered key needs for every name to keep one
 * line: the index track, the gap, and the widest name, plus whatever follows
 * the name. console.css sets the key in two such columns where both fit.
 */
export function keyColumnWidth(names: string[], after: number = 0): number {
  /** The index track of a key (2.5ch of --text-n1), in px. */
  const index = 20;
  return index + KEY_GAP + Math.ceil(Math.max(0, ...names.map((name) => textWidthBound(name.trim())))) + after;
}
/** The gap between the tracks of a key row (--space-8px), in px. */
export const KEY_GAP = 8;

/**
 * What holds the plot of a column chart. A plain axis needs nothing but the
 * canvas. A wrapped or numbered one adds the list of names under the plot,
 * numbered in category order, which console.css shows wherever the axis shows
 * numbers.
 */
export function CategoryFrame({ plan, labels, children }: { plan: CategoryAxisPlan; labels: string[]; children: ReactNode }) {
  const canvas = (
    <div className="con-chart-canvas" aria-hidden="true">
      {children}
    </div>
  );
  if (plan.mode === "plain") return canvas;
  return (
    <div className="con-col" data-ticks={plan.mode} data-names-from={plan.namesFrom} data-whole-from={plan.wholeFrom}>
      <div className="con-col-stage">{canvas}</div>
      <ol className="con-col-key" style={{ "--con-key-col": `${keyColumnWidth(labels)}px` } as CSSProperties}>
        {labels.map((label, i) => (
          <li key={i} className="con-col-key-item">
            <span className="con-col-key-index">{i + 1}</span>
            <span className="con-col-key-label">{label}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export interface ColumnChartProps extends ChartBaseProps {
  /**
   * Categories in their natural order (weeks, months, short region codes, the
   * buckets of a histogram). A label of 8 characters or less prints on one
   * line as it always has. A longer one wraps onto two lines at a word
   * boundary, up to COLUMN_LABEL_MAX characters and COLUMN_CATEGORIES_MAX
   * categories; columnLabelsFit says whether a set of labels prints at every
   * width. Long unordered names still belong in RankedBars.
   */
  data: CategoryDatum[];
  /** Label of the one column to emphasise; the others step back to a tint. */
  highlight?: string;
  height?: ChartHeight;
}

/** Vertical columns for an ordered category axis. */
export function ColumnChart({ label, summary, valueLabel, categoryLabel, format = defaultFormat, data, highlight, height = "md" }: ColumnChartProps) {
  const values = data.map((d) => d.value);
  const labels = data.map((d) => d.label);
  const yWidth = tickAxisWidth(values, format);
  const plan = planCategoryAxis(labels, yWidth);
  return (
    <ChartFigure
      label={label}
      summary={summary}
      kind="column"
      height={height}
      table={{ caption: label, head: [categoryLabel, valueLabel], rows: data.map((d) => [d.label, format(d.value)]) }}
    >
      <CategoryFrame plan={plan} labels={labels}>
        <BarChart responsive style={FILL} data={data} margin={{ top: 8, right: PLOT_END, bottom: 0, left: 0 }} accessibilityLayer={false}>
          <CartesianGrid vertical={false} />
          {plan.mode === "plain" ? (
            <XAxis dataKey="label" tickLine={false} tick={TICK} interval="preserveStartEnd" minTickGap={4} height={28} />
          ) : (
            <XAxis {...categoryAxisProps(plan)} />
          )}
          <YAxis tickLine={false} axisLine={false} tick={TICK} tickCount={4} allowDecimals={false} width={yWidth} tickFormatter={(v: number) => format(v)} />
          <Tooltip cursor={{ fill: "var(--color-layer-1)" }} content={<ChartTooltip format={format} />} isAnimationActive={false} />
          <Bar dataKey="value" name={valueLabel} isAnimationActive={false} radius={[4, 4, 0, 0]} maxBarSize={48}>
            {data.map((d) => (
              <Cell key={d.label} fill={highlight === undefined || d.label === highlight ? SERIES_MAIN : SERIES_CONTEXT} />
            ))}
          </Bar>
        </BarChart>
      </CategoryFrame>
    </ChartFigure>
  );
}

export interface TrendSeries {
  /** Key in each datum. */
  key: string;
  /** Legend and table header. */
  label: string;
}

export type TrendDatum = { label: string } & Record<string, number | string>;

export interface TrendChartProps extends Omit<ChartBaseProps, "valueLabel"> {
  /** Points in time order; `label` is the x value ("1 Agu", "Mg 3"). */
  data: TrendDatum[];
  /** 1 to 3 series. One series draws an area, more draw lines with a legend. */
  series: TrendSeries[];
  height?: ChartHeight;
  /**
   * How one series is drawn: an area (default); a line with a ring at every
   * point; or a step, the value held flat for its whole period and changing
   * at the next, the honest reading of a monthly total. Two or three series
   * are always lines.
   */
  mark?: "area" | "line" | "step";
}

/** A trend over time: an area for one series, lines for two or three. */
export function TrendChart({ label, summary, categoryLabel, format = defaultFormat, data, series, height = "md", mark = "area" }: TrendChartProps) {
  const single = series.length === 1 ? mark : null;
  const values = data.flatMap((d) => series.map((s) => Number(d[s.key]) || 0));
  const first = data[0]?.label;
  const last = data[data.length - 1]?.label;
  const table = {
    caption: label,
    head: [categoryLabel, ...series.map((s) => s.label)],
    rows: data.map((d) => [d.label, ...series.map((s) => format(Number(d[s.key]) || 0))]),
  };
  const yWidth = yAxisWidth(values, format);
  const common = (
    <>
      <CartesianGrid vertical={false} />
      <XAxis dataKey="label" tickLine={false} tick={<EdgeTick first={first} last={last} />} interval="preserveStartEnd" minTickGap={24} height={28} />
      <YAxis tickLine={false} axisLine={false} tick={TICK} tickCount={4} allowDecimals={false} width={yWidth} tickFormatter={(v: number) => format(v)} />
      <Tooltip cursor={{ stroke: "var(--color-border-strong)" }} content={<ChartTooltip format={format} />} isAnimationActive={false} />
    </>
  );
  const legend =
    series.length > 1 ? (
      <ul className="con-legend" aria-hidden="true">
        {series.map((s, i) => (
          <li key={s.key} className="con-legend-item">
            <span className="con-swatch" data-shape="line" style={{ background: SERIES_COLORS[i] }} />
            <span>{s.label}</span>
          </li>
        ))}
      </ul>
    ) : undefined;
  return (
    <ChartFigure label={label} summary={summary} kind={single === "step" ? "step" : "trend"} height={height} table={table} legend={legend}>
      <div className="con-chart-canvas" aria-hidden="true">
        {single === "line" || single === "step" ? (
          <LineChart responsive style={FILL} data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }} accessibilityLayer={false}>
            {common}
            <Line
              type={single === "step" ? "stepAfter" : "linear"}
              dataKey={series[0]!.key}
              name={series[0]!.label}
              stroke={SERIES_MAIN}
              strokeWidth={2}
              dot={single === "line" ? { r: 3, strokeWidth: 2, fill: "var(--color-surface)", stroke: SERIES_MAIN } : false}
              activeDot={{ r: 4, strokeWidth: 0, fill: SERIES_MAIN }}
              isAnimationActive={false}
            />
          </LineChart>
        ) : series.length === 1 ? (
          <AreaChart responsive style={FILL} data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }} accessibilityLayer={false}>
            {common}
            <Area
              type="monotone"
              dataKey={series[0].key}
              name={series[0].label}
              stroke={SERIES_MAIN}
              strokeWidth={2}
              fill={SERIES_MAIN}
              fillOpacity={0.12}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0, fill: SERIES_MAIN }}
              isAnimationActive={false}
            />
          </AreaChart>
        ) : (
          <LineChart responsive style={FILL} data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }} accessibilityLayer={false}>
            {common}
            {series.map((s, i) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={SERIES_COLORS[i]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0, fill: SERIES_COLORS[i] }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        )}
      </div>
    </ChartFigure>
  );
}

export interface SparklineProps {
  values: number[];
  /** The trend in words for assistive tech: "8 minggu terakhir, naik dari 12 ke 31". */
  label: string;
}

/** A word-sized trend line with no axes, for a StatTile. Hidden from assistive tech; `label` says it. */
export function Sparkline({ values, label }: SparklineProps) {
  const data = values.map((v, i) => ({ i, v }));
  return (
    <span className="con-spark" role="img" aria-label={label}>
      <span className="con-spark-plot con-chart-canvas" aria-hidden="true">
        <LineChart responsive style={FILL} data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }} accessibilityLayer={false}>
          <Line type="monotone" dataKey="v" stroke={SERIES_MAIN} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </span>
    </span>
  );
}

export type { ValueFormat };
